import { useCallback, useEffect, useRef, useState } from 'react';
import { Bell, CheckCheck, ExternalLink } from 'lucide-react';
import { formatEuro } from '../../utils/currency';
import {
  appNotificationService,
  invoiceRequestService,
  type AppNotification,
  type InvoiceRequest,
} from '../../services/invoiceRequestService';
import { log } from '../../lib/logger';

const POLL_MS = 60_000;

const STATUS_CHIP: Record<InvoiceRequest['status'], { label: string; className: string }> = {
  pending: { label: 'Por emitir', className: 'cm-chip cm-chip-warning' },
  done: { label: 'Concluído', className: 'cm-chip cm-chip-success' },
  cancelled: { label: 'Cancelado', className: 'cm-chip cm-chip-neutral' },
};

function timeAgo(iso: string): string {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return 'agora';
  if (minutes < 60) return `há ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `há ${hours} h`;
  return new Date(iso).toLocaleDateString('pt-PT', { day: 'numeric', month: 'short' });
}

/** Main-app notifications. Today: invoice requests, with what's needed to issue them. */
export function NotificationBell({ onOpenCampaign }: { onOpenCampaign: (campaignId: string) => void }) {
  const [open, setOpen] = useState(false);
  const [available, setAvailable] = useState(true);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [requests, setRequests] = useState<Map<string, InvoiceRequest>>(new Map());
  const rootRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      const list = await appNotificationService.list();
      setNotifications(list);
      const ids = [...new Set(list.map(n => n.invoice_request_id).filter((id): id is string => !!id))];
      const found = await invoiceRequestService.listByIds(ids);
      setRequests(new Map(found.map(r => [r.id, r])));
      setAvailable(true);
    } catch (err) {
      // Tables not there yet (SQL not applied): no bell rather than an error
      log.warn('Notifications not loaded:', err);
      setAvailable(false);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = window.setInterval(load, POLL_MS);
    window.addEventListener('focus', load);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', load);
    };
  }, [load]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!available) return null;

  const unread = notifications.filter(n => !n.read_at);

  async function markRead(ids: string[]) {
    const now = new Date().toISOString();
    setNotifications(prev => prev.map(n => ids.includes(n.id) && !n.read_at ? { ...n, read_at: now } : n));
    try {
      await appNotificationService.markRead(ids);
    } catch (err) {
      log.warn('Could not mark notifications read:', err);
    }
  }

  function openNotification(n: AppNotification) {
    if (!n.read_at) markRead([n.id]);
    if (n.campaign_id) {
      setOpen(false);
      onOpenCampaign(n.campaign_id);
    }
  }

  return (
    <div ref={rootRef} className="relative z-40">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="relative flex items-center justify-center w-10 h-10 rounded-full bg-white shadow-card hover:shadow transition-shadow"
        aria-label={unread.length > 0 ? `Notificações, ${unread.length} por ler` : 'Notificações'}
        aria-expanded={open}
      >
        <Bell className="w-5 h-5 text-gray-700" />
        {unread.length > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center tabular-nums">
            {unread.length > 9 ? '9+' : unread.length}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[min(92vw,400px)] bg-white rounded-2xl shadow-float ring-1 ring-gray-100 overflow-hidden" role="dialog" aria-label="Notificações">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-bold text-brand-ink">Notificações</p>
            {unread.length > 0 && (
              <button type="button" onClick={() => markRead(unread.map(n => n.id))} className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-brand-ink">
                <CheckCheck className="w-3.5 h-3.5" />
                Marcar como lidas
              </button>
            )}
          </div>

          <div className="max-h-[70vh] overflow-y-auto divide-y divide-gray-100">
            {notifications.length === 0 && (
              <p className="px-4 py-8 text-center text-sm text-gray-400">Sem notificações.</p>
            )}
            {notifications.map(n => {
              const request = n.invoice_request_id ? requests.get(n.invoice_request_id) : undefined;
              const chip = request ? STATUS_CHIP[request.status] : null;
              return (
                <div key={n.id} className={`px-4 py-3 space-y-1.5 ${n.read_at ? '' : 'bg-brand-lime/10'}`}>
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold text-brand-ink">
                      {!n.read_at && <span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-1.5 align-middle" aria-hidden />}
                      {n.title}
                    </p>
                    <span className="text-[11px] text-gray-400 whitespace-nowrap">{timeAgo(n.created_at)}</span>
                  </div>
                  {request ? (
                    <div className="text-xs text-gray-600 space-y-0.5">
                      <p className="text-gray-800">{request.campaign_name}</p>
                      <p>
                        <strong className="text-gray-800">{request.client_name || 'Cliente sem nome'}</strong>
                        {request.client_vat && <> · NIF {request.client_vat}</>}
                      </p>
                      {request.client_address && <p className="text-gray-500">{request.client_address}</p>}
                      <p className="tabular-nums">
                        {request.lines.map((l, i) => (
                          <span key={l.id}>{i > 0 && ' + '}{formatEuro(l.amount)}{l.fulfilled_at && ' ✓'}</span>
                        ))}
                        {request.lines.length > 1 && <> = <strong>{formatEuro(request.total)}</strong></>}
                        <span className="text-gray-400"> sem IVA</span>
                      </p>
                      {request.purchase_order && <p>Ordem de compra: {request.purchase_order}</p>}
                      {request.notes && <p className="whitespace-pre-wrap bg-gray-50 rounded-lg px-2 py-1.5 text-gray-700">{request.notes}</p>}
                    </div>
                  ) : n.body ? (
                    <p className="text-xs text-gray-600">{n.body}</p>
                  ) : null}
                  <div className="flex items-center justify-between gap-2 pt-0.5">
                    {chip ? <span className={chip.className}>{chip.label}</span> : <span />}
                    {n.campaign_id && (
                      <button type="button" onClick={() => openNotification(n)} className="inline-flex items-center gap-1 text-xs font-semibold text-brand-ink hover:underline">
                        Abrir campanha
                        <ExternalLink className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
